import React from 'react';
import { CustomStepProps } from '../../types/survey';

export const NutritionAssessmentResults: React.FC<CustomStepProps> = ({
  onNext,
  onBack,
  surveyData,
}) => {
  // Calculate fast food score (reverse scoring - lower consumption is better)
  const calculateFastFoodScore = () => {
    const answers = surveyData?.answers || {};
    const fastFood = answers.fast_food_consumption;
    
    // Reverse scoring: Never (1) gets 5 points, Daily (5) gets 1 point
    if (fastFood === 1) return 5; // Never - 5 points
    if (fastFood === 2) return 4; // Rarely - 4 points
    if (fastFood === 3) return 3; // Sometimes - 3 points
    if (fastFood === 4) return 2; // Often - 2 points
    if (fastFood === 5) return 1; // Daily - 1 point
    
    return 0; // Default if no answer
  };

  // Calculate fruits and vegetables + nutrition rating score
  const calculateHealthyEatingScore = () => {
    const answers = surveyData?.answers || {};
    let score = 0;
    
    // Fruits and vegetables (direct rating value 1-5)
    const fruitsVeg = answers.fruits_and_vegetables;
    if (fruitsVeg) score += fruitsVeg;
    
    // Nutrition self-rating (direct star rating value 1-5)
    const nutritionRating = answers.nutrition_rating;
    if (nutritionRating) score += nutritionRating;
    
    return score;
  };

  const fastFoodScore = calculateFastFoodScore();
  const healthyEatingScore = calculateHealthyEatingScore();
  
  const hasFastFoodIssues = fastFoodScore < 4;
  const hasHealthyEatingIssues = healthyEatingScore < 6;

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue-lightest via-white to-royal-gold-lightest flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/30 rounded-full mb-4">
                <svg className="w-10 h-10 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold mb-4 text-gray-800">
                Nutrition Assessment Results
              </h1>
              <p className="text-xl text-gray-700">
                Your personalized dietary analysis
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Score Display */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-2xl p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2 text-gray-800">{fastFoodScore}/5</div>
                  <div className="text-sm text-gray-700">Avoiding Unhealthy Foods</div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-royal-blue-lightest to-royal-gold rounded-2xl p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2 text-gray-800">{healthyEatingScore}/10</div>
                  <div className="text-sm text-gray-700">Healthy Eating Habits</div>
                </div>
              </div>
            </div>

            {/* Fast Food Assessment */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Junk Food Consumption
              </h2>
              <div className={`${hasFastFoodIssues ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <h3 className="text-lg font-semibold mb-3 ${hasFastFoodIssues ? 'text-amber-900' : 'text-green-900'}">
                  {hasFastFoodIssues ? 'You can do much better!' : 'Excellent! Keep it up!'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {hasFastFoodIssues ? (
                    <>
                      Your answers indicate that you do not always manage to eat healthily. If you want to enjoy a longer life, 
                      have more vitality, and fulfill your passions and dreams, it's worth to improve upon this area. 
                      Our consultants will be happy to set up an individual nutrition plan for you and help you permanently 
                      change your eating habits for the better.
                    </>
                  ) : (
                    <>
                      A healthy diet is the foundation for optimal health and a satisfying quality of life. 
                      By taking care of your nutrition properly on a daily basis, you cope better with the consequences of stress, 
                      you look better, your brain works better and, above all, you have the energy to pursue your passions. Well done!
                    </>
                  )}
                </p>
              </div>
              
              {hasFastFoodIssues && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Health Risk Alert</h3>
                      <p className="mt-1 text-sm text-red-700">
                        Did you know that people who regularly consume junk food are 51% more likely to suffer from depression? 
                        If you want to work on your metabolic efficiency and the quality of your microbiome - contact us.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Healthy Eating Assessment */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Fruits, Vegetables & Overall Nutrition
              </h2>
              <div className={`${hasHealthyEatingIssues ? 'bg-amber-50' : 'bg-green-50'} rounded-lg p-6`}>
                <h3 className="text-lg font-semibold mb-3 ${hasHealthyEatingIssues ? 'text-amber-900' : 'text-green-900'}">
                  {hasHealthyEatingIssues ? 'You can do much better!' : 'Well done!'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {hasHealthyEatingIssues ? (
                    <>
                      Your answers indicate that you do not always manage to eat healthily. If you want to enjoy a longer life, 
                      have more vitality, and fulfill your passions and dreams, it's worth to improve upon this area. 
                      Our consultants will be happy to set up an individual nutrition plan for you and help you permanently 
                      change your eating habits for the better.
                    </>
                  ) : (
                    <>
                      What a great result! The right diet provides you with all the nutrients necessary for growth, development 
                      and for the body to recover. According to the World Health Organization, there are about 80 diet-related diseases, 
                      which include obesity, cancer, osteoporosis and iron deficiency anemia. Your score indicates that you understand 
                      the importance of fruit and vegetables in your diet. Keep it up!
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Expert Recommendations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Our Experts Recommend
              </h2>
              <div className="bg-gradient-to-r from-royal-gold to-royal-blue-lightest rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Aim for at least 5 portions of fruits and vegetables daily for optimal health
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Choose whole foods over processed options whenever possible
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Stay hydrated with water instead of sugary drinks
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-royal-gold mr-3 text-xl">•</span>
                    <p className="text-gray-700">
                      Plan your meals to avoid impulsive unhealthy food choices
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Nutrition Tips Grid */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Nutrition Principles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Balanced Macros</span>
                      <p className="text-xs text-gray-600 mt-1">Proteins, carbs, and healthy fats</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Micronutrients</span>
                      <p className="text-xs text-gray-600 mt-1">Essential vitamins and minerals</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-gold-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Meal Timing</span>
                      <p className="text-xs text-gray-600 mt-1">Regular eating schedule</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-royal-blue-lightest rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Portion Control</span>
                      <p className="text-xs text-gray-600 mt-1">Mindful eating habits</p>
                    </div>
                    <svg className="w-8 h-8 text-royal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={onBack}
                className="px-8 py-3 bg-white border-2 border-royal-gold text-royal-gold font-semibold rounded-lg hover:bg-royal-gold hover:text-white transition-colors"
              >
                Previous
              </button>
              <button
                onClick={onNext}
                className="px-8 py-3 bg-royal-gold text-white font-semibold rounded-lg hover:bg-royal-gold-dark transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};